import { useEffect, useState } from "react";
import { http, apiError, rp } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Ui";
import UploadBox from "@/components/UploadBox";
import { Save } from "lucide-react";

const KATEGORI = ["Produk Sudah Bergulir", "Produk Dalam Pengembangan", "Produk Baru Diversifikasi"];
const PORTS = ["Tanjung Perak", "Tanjung Priok", "Belawan", "Makassar", "Balikpapan"];

const empty = {
  kode_stock: "", deskripsi_produk: "", type_produk: "", spesifikasi_produk: "",
  qty: "", harga_beli_usd: "", kurs: "", supplier: "", no_po: "", no_packing_list: "",
  container: "", pelabuhan_asal: "Shanghai", tujuan_pelabuhan: PORTS[0], eta: "",
  status_shipment: "On The Way", kategori_pemasaran: KATEGORI[0], catatan: "",
};

export default function EtaEntry() {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);

  const load = () => http.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const idr = (Number(f.harga_beli_usd) || 0) * (Number(f.kurs) || 0);
  const total = idr * (Number(f.qty) || 0);

  const autofill = async (kode) => {
    if (!kode) return;
    try {
      const { data } = await http.get(`/products/${kode.toUpperCase()}`);
      setF((s) => ({ ...s, deskripsi_produk: data.deskripsi_produk || "", type_produk: data.type_produk || "",
        spesifikasi_produk: data.spesifikasi_produk || "", kategori_pemasaran: data.kategori_pemasaran || s.kategori_pemasaran }));
      toast.success(`Data produk ${kode.toUpperCase()} terisi otomatis`);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/eta", {
        ...f, kode_stock: f.kode_stock.toUpperCase(),
        qty: Number(f.qty) || 0, harga_beli_usd: Number(f.harga_beli_usd) || 0,
        kurs: Number(f.kurs) || 0, harga_beli_idr: idr, total_nominal: total,
      });
      toast.success("Data ETA tersimpan");
      setF(empty); load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const inp = "w-full h-10 px-3 border border-neutral-300 bg-white outline-none focus:ring-2 focus:ring-[#4338CA] focus:border-transparent text-sm";
  const lbl = "text-[11px] font-semibold uppercase tracking-wide text-[#525252]";
  const fld = ({ label, tid, k, type = "text", ...rest }) => (
    <div>
      <label className={lbl}>{label}</label>
      <input data-testid={tid} type={type} className={inp + (type === "number" ? " mono text-right" : "")}
        value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...rest} />
    </div>
  );

  return (
    <div data-testid="eta-entry-page">
      <PageHeader subtitle="Dept Purchasing · Kantor Pusat" title="Entry & Upload Estimasi Kedatangan (ETA)" testId="eta-entry-header" />
      <div className="p-6 sm:p-8 space-y-4">
        <UploadBox endpoint="/eta/upload" templateKind="eta" onDone={load} testId="eta-upload" />

        <form onSubmit={submit} className="erp-panel p-5 space-y-5" data-testid="eta-entry-form">
          <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">Entry Manual Update ETA</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={lbl}>Kode Stock</label>
              <input data-testid="eta-kode-input" required list="prodlist2" className={inp + " uppercase"}
                value={f.kode_stock} onChange={(e) => setF({ ...f, kode_stock: e.target.value })}
                onBlur={(e) => autofill(e.target.value)} placeholder="KSFO022" />
              <datalist id="prodlist2">
                {products.map((p) => <option key={p.kode_stock} value={p.kode_stock}>{p.deskripsi_produk}</option>)}
              </datalist>
            </div>
            {fld({ label: "Deskripsi Produk", tid: "eta-deskripsi-input", k: "deskripsi_produk" })}
            {fld({ label: "Type Produk", tid: "eta-type-input", k: "type_produk" })}
            {fld({ label: "Spesifikasi Produk", tid: "eta-spec-input", k: "spesifikasi_produk" })}

            {fld({ label: "Qty", tid: "eta-qty-input", k: "qty", type: "number", placeholder: "0" })}
            {fld({ label: "Harga Beli / Unit USD", tid: "eta-usd-input", k: "harga_beli_usd", type: "number", step: "0.01", placeholder: "0" })}
            {fld({ label: "Kurs USD-IDR", tid: "eta-kurs-input", k: "kurs", type: "number", placeholder: "16000" })}
            <div>
              <label className={lbl}>Harga Beli / Unit Rupiah</label>
              <div data-testid="eta-idr-display" className="h-10 px-3 border border-neutral-200 bg-neutral-50 flex items-center justify-end mono text-sm">{rp(idr)}</div>
            </div>

            {fld({ label: "Nama Supplier", tid: "eta-supplier-input", k: "supplier" })}
            {fld({ label: "No. PO", tid: "eta-po-input", k: "no_po" })}
            {fld({ label: "No. Packing List", tid: "eta-packing-input", k: "no_packing_list" })}
            {fld({ label: "Container / Loading", tid: "eta-container-input", k: "container", placeholder: "FCL - 1x40'" })}

            {fld({ label: "Pelabuhan Asal", tid: "eta-asal-input", k: "pelabuhan_asal" })}
            <div>
              <label className={lbl}>Tujuan Pelabuhan</label>
              <select data-testid="eta-tujuan-select" className={inp} value={f.tujuan_pelabuhan}
                onChange={(e) => setF({ ...f, tujuan_pelabuhan: e.target.value })}>
                {PORTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {fld({ label: "Estimasi Barang Tiba (ETA)", tid: "eta-date-input", k: "eta", type: "date" })}
            <div>
              <label className={lbl}>Kategori Pemasaran Produk</label>
              <select data-testid="eta-kategori-select" className={inp} value={f.kategori_pemasaran}
                onChange={(e) => setF({ ...f, kategori_pemasaran: e.target.value })}>
                {KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 border-t border-neutral-200 pt-4">
            <div className="flex-1">
              <div className={lbl}>Total Nominal per Produk</div>
              <div data-testid="eta-total-display" className="mono text-2xl font-semibold">{rp(total)}</div>
            </div>
            <input data-testid="eta-catatan-input" className={inp + " sm:max-w-xs"} placeholder="Catatan"
              value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
            <button data-testid="eta-submit-button" disabled={busy}
              className="h-10 px-6 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#3730A3] transition-colors disabled:opacity-60">
              <Save className="h-3.5 w-3.5" /> Simpan Data ETA
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
